import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, InitialNavigation, Router } from '@angular/router';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../../../shared/controls/pageable-table/pageable-table.component';

import { DialogService } from '../../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../shared/services/error-service';
import { InitializerService } from '../../../../shared/services/initializer-service';
import { LoadingHelper } from '../../../../shared/services/loading-helper';
import { LoadingService } from '../../../../shared/services/loading.service';
import { EndorsementTypeConstants } from '../../../../shared/constants/endorsement-type.constants';

import { PagingData, EndorsementService, AllEndorsementTypeItem, ClientAreaService, EndorsementByClientAreaIdItem } from '../../../../../service/proxy';

@Component({
    selector: 'client-area-endorsement-list',
    templateUrl: './client-area-endorsement.component.html',
    styleUrl: './client-area-endorsement.component.scss',

    imports: [
        CommonModule,
        PageableTableComponent,
        FormsModule
    ],

    providers: [
        EndorsementService,
        ClientAreaService
    ]
})
export class EndorsementComponent implements OnInit {
    public clientId: number = 0;
    public policyId: number = 0;

    public endorsements: EndorsementByClientAreaIdItem[] = [];

    public endorsementTypes: AllEndorsementTypeItem[] = [];
    private inputTypeCache: { [key: number]: string } = {};

    public search: string = '';
    public paging: PagingData = new PagingData();

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private dialogService: DialogService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private endorsementService: EndorsementService,
        private initializerService: InitializerService,
        private clientAreaService: ClientAreaService,
        private loadingService: LoadingService,
        private cdr: ChangeDetectorRef
    )
    {
        this.paging = this.initializerService.createPagingData();
        this.paging.sortColumn = 'endorsementType';
        this.paging.sortDirection = 'ASC';
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            this.clientId = Number(params.get('clientId'));
            this.policyId = Number(params.get('policyId'));
        });
    }

    loadEndorsementTypes(): Promise<void> {
        return new Promise((resolve) => {
            this.endorsementService.getAllEndorsementTypes()
                .subscribe((data) => {
                    this.endorsementTypes = data.results || [];
                    resolve();
                });
        });
    }

    async formatValueForDisplay(value: string | number, endorsementType: string): Promise<string> {
        if (!value && value !== 0) return '';

        const valueStr = value.toString();

        const selectedType = this.endorsementTypes.find(e => e.description === endorsementType);

        if (!selectedType) return valueStr;

        const inputType = await this.getInputTypeForEndorsementTypeId(selectedType.id);

        switch (inputType) {
            case 'rand':
                return `R${valueStr}`;
            case 'percent':
                return `${valueStr}%`;
            default:
                return valueStr;
        }
    }

    getInputTypeForEndorsementTypeId(endorsementTypeId: number): Promise<string> {
        if (this.inputTypeCache[endorsementTypeId]) {
            return Promise.resolve(this.inputTypeCache[endorsementTypeId]);
        }

        return new Promise((resolve) => {
            this.endorsementService.getFilteredEndorsementTypeValues(endorsementTypeId)
                .subscribe({
                    next: (data) => {
                        let inputType = data.results.length > 1 ? 'dropdown' : 'text';

                        if (inputType === 'text') {
                            const singleResult = data.results[0];

                            if (singleResult && singleResult.description) {
                                switch (singleResult.description) {
                                    case 'R':
                                        inputType = 'rand';
                                        break;
                                    case '%':
                                        inputType = 'percent';
                                        break;
                                    case 'Number':
                                        inputType = 'number';
                                        break;
                                    case 'Multiline-Text':
                                        inputType = 'multiline-text';
                                        break;
                                    default:
                                        inputType = 'text';
                                        break;
                                }
                            }
                        }

                        this.inputTypeCache[endorsementTypeId] = inputType;
                        resolve(inputType);
                    },
                    error: () => {
                        this.inputTypeCache[endorsementTypeId] = 'text';
                        resolve('text');
                    }
                });
        });
    }

    getEndorsements(pagingData: PagingData = new PagingData()): void {
        pagingData.sortColumn = pagingData.sortColumn || 'endorsementType';

        const request$ = this.clientAreaService.getEndorsementsByClientAreaId(
            this.policyId,
            this.search,
            pagingData.pageNo,
            pagingData.pageSize,
            pagingData.sortDirection,
            pagingData.sortColumn
        );

        LoadingHelper.withLoading(request$, this.loadingService)
            .subscribe({
                next: async (data) => {
                    this.paging = data.pagingData;

                    if (this.endorsementTypes.length === 0) {
                        await this.loadEndorsementTypes();
                    }

                    const processedEndorsements = await Promise.all((data.results || []).map(async (item) => {
                        const expiry = new Date(item.expiryDate);
                        const isMaxDate = expiry.getFullYear() === 9999 && expiry.getMonth() === 11 && expiry.getDate() === 31;
                        const today = new Date();

                        item.effectiveDate = formatDate(item.effectiveDate, 'yyyy/MM/dd', 'en-US') as any;
                        item.expiryDate = isMaxDate
                            ? 'No expiry date'
                            : formatDate(expiry, 'yyyy/MM/dd', 'en-US') as any;

                        item.values = await this.formatValueForDisplay(item.values, item.endorsementType);
                        item.endorsementType = EndorsementTypeConstants.getDisplayName(item.endorsementType);

                        expiry.setHours(0, 0, 0, 0);
                        today.setHours(0, 0, 0, 0);

                        return {
                            ...(item as any),
                            isExpired: isMaxDate
                                ? false
                                : today >= expiry
                        };
                    }));

                    this.endorsements = processedEndorsements;
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.endorsements = [];
                        this.paging = this.initializerService.createPagingData();
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                    this.cdr.detectChanges();
                }
            });
    }

   upsertEndorsement(endorsementId: any): void {
        const route = [`client-area/${this.clientId}/${this.policyId}/${endorsementId}`];

        const extras = endorsementId.isExpired ? { queryParams: { viewOnly: true } } : {};
        this.router.navigate(route, extras);
    }

    getColumnMap() {
        return {
            endorsementType: 'Endorsement Type',
            values: 'Value',
            effectiveDate: 'Effective Date',
            expiryDate: 'Expiry Date'
        };
    }

    deleteEndorsement(id: number): void {
        this.dialogService.show('Delete', 'Are you sure you want to delete this record?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.endorsementService.deleteEndorsement(id)
                        .subscribe({
                            next: (result: any) => {
                                this.toastService.show('Endorsement deleted successfully', 'success');
                                this.getEndorsements(this.paging);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            }
                        });
                }
            }
        );
    }
}